/**
 * 卡片注销 - 验证码输入页面
 */

import React, { useState, useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Toast } from 'react-vant'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomButton from '@/components/CustomButton'
import CustomTextInput from '@/components/CustomTextInput'
import VerificationCodeInput from '@/components/VerificationCodeInput'
import { VerActionType } from '@/components/SelectVerification'
import { useSendCode } from '@/hooks/useSendCode'
import { useUserInfo } from '@/hooks'
import { APIWeb } from '@/api'

// 注销信息接口
interface CancelInfo {
  cardId: string
  cardNo: string
  verificationMethod: VerActionType
  account: string
}

export default React.memo(function CardCancelVerifyCode() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { state } = useLocation()
  const { userInfo } = useUserInfo()
  const { sendCode, sendCodeLoading } = useSendCode()

  // 从路由 state 获取注销信息
  const cancelInfo: CancelInfo = state || {}

  const [submitLoading, setSubmitLoading] = useState(false)
  // 谷歌验证码
  const [googleCode, setGoogleCode] = useState('')

  // 是否是谷歌验证
  const isGoogleVerification = cancelInfo.verificationMethod === VerActionType.GoogleVer

  // 谷歌验证码输入处理
  const handleGoogleCodeChange = useCallback((value: string) => {
    setGoogleCode(value)
  }, [])

  // 提交验证码
  const handleSubmit = useCallback(async (code: string) => {
    if (!cancelInfo.cardId || !code) {
      Toast.info(t('参数错误'))
      return
    }

    try {
      setSubmitLoading(true)

      // 第一步：验证验证码并获取 verify_key
      const verifyResult = await APIWeb.verifyOldAccountCode({
        account: isGoogleVerification ? 'google' : cancelInfo.account,
        scene: 'card_close',
        code,
      })

      const verifyKey = verifyResult?.verify_key || code

      // 第二步：调用注销 API
      await APIWeb.closeCardV2({
        card_id: Number(cancelInfo.cardId),
        verify_key: verifyKey,
      })

      Toast.success(t('注销成功'))

      // 返回卡片主页
      navigate('/card', { replace: true })
    } catch (error: any) {
      console.error('注销失败:', error)
      Toast.fail(error?.message || t('注销失败'))
    } finally {
      setSubmitLoading(false)
    }
  }, [cancelInfo, navigate, t, isGoogleVerification])

  // 重新发送验证码
  const handleResendCode = useCallback(async () => {
    if (isGoogleVerification) {
      return
    }

    const account = cancelInfo.verificationMethod === VerActionType.PhoneVer
      ? userInfo?.phone || ''
      : userInfo?.email || ''

    await sendCode({
      account,
      scene: 'card_cancel'
    })
  }, [isGoogleVerification, cancelInfo.verificationMethod, userInfo, sendCode])

  // 谷歌验证器输入组件
  const googleInputVer = useMemo(() => {
    return (
      <div>
        <div className="space-y-2">
          <div className="text-3 text-[#1A1A1A]/60 mb-1">{t('请输入谷歌验证码')}</div>
          <CustomTextInput
            value={googleCode}
            onChangeText={handleGoogleCodeChange}
            placeholder={t('请输入谷歌验证码')}
          />
        </div>
        <div className="mt-6">
          <CustomButton
            loading={submitLoading}
            disabled={submitLoading || !googleCode}
            onClick={() => handleSubmit(googleCode)}
          >
            {t('确认注销')}
          </CustomButton>
        </div>
      </div>
    )
  }, [googleCode, handleGoogleCodeChange, submitLoading, handleSubmit, t])

  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={<span className="text-base font-medium">{t('卡片注销')}</span>}
      />

      <div className="px-6 py-6">
        {isGoogleVerification ? (
          googleInputVer
        ) : (
          <VerificationCodeInput
            account={cancelInfo.account || ''}
            resendLoading={sendCodeLoading}
            submitLoading={submitLoading}
            onResendCode={handleResendCode}
            onSubmit={handleSubmit}
            submitButtonText={t('确认')}
          />
        )}
      </div>
    </PageLayout>
  )
})

